"use client";

import Image from "next/image";
import { Plus } from "lucide-react";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import type { Media } from "@/payload-types";
import { NAV_SVG_ICONS } from "@/components/icons/nav";
import { cn } from "@/lib/utils";

type UIAssetLike =
  | {
      slug?: string | null;
      assetType?: "static" | "lottie" | null;
      posterImage?: Media | string | number | null;
      lottieFile?: Media | string | number | null;
    }
  | string
  | null
  | undefined;

interface NavIconProps {
  label: string;
  iconAsset?: UIAssetLike;
  legacyIcon?: Media | string | number | null;
  active?: boolean;
  collapsed?: boolean;
  size?: number;
  play?: boolean;
}

function getMedia(media: Media | string | number | null | undefined) {
  return typeof media === "object" && media !== null ? media : null;
}

export function NavIcon({
  label,
  iconAsset,
  legacyIcon,
  collapsed = false,
  size = 66,
  play = true,
}: NavIconProps) {
  const asset = typeof iconAsset === "object" && iconAsset !== null ? iconAsset : null;
  const poster = getMedia(asset?.posterImage) ?? getMedia(legacyIcon);
  const lottieFile = getMedia(asset?.lottieFile);
  const isLottie = asset?.assetType === "lottie" && !!lottieFile?.url;
  const slug = asset?.slug ?? null;
  const SvgIcon = slug ? NAV_SVG_ICONS[slug] : null;

  if (!SvgIcon && !poster?.url && !lottieFile?.url) {
    return null;
  }

  return (
    <div
      className={cn(
        "relative flex-shrink-0 transition-all duration-300",
        collapsed
          ? "h-6 w-6"
          : "h-14 w-14 3xl:h-[66px] 3xl:w-[66px]"
      )}
    >
      {/* Full icon — visible when expanded */}
      <div className={`absolute inset-0 transition-opacity duration-300 ${collapsed ? "opacity-0" : "opacity-100"}`}>
        {SvgIcon ? (
          <SvgIcon
            size={size}
            play={play && !collapsed}
            className="h-full w-full text-white"
          />
        ) : (
          <>
            {poster?.url && (
              <Image
                src={poster.url}
                alt={poster.alt || label}
                width={size}
                height={size}
                className={`h-full w-full object-contain transition-opacity ${
                  isLottie ? "opacity-0" : "opacity-100"
                }`}
              />
            )}

            {isLottie && lottieFile?.url && (
              <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
              >
                <DotLottieReact
                  src={lottieFile.url}
                  autoplay
                  loop
                  className="h-full w-full"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Compact placeholder — visible when collapsed */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${collapsed ? "opacity-100" : "opacity-0"}`}>
        <Plus
          className="h-5 w-5 text-white/80 transition-transform duration-300 group-hover:rotate-90"
          aria-hidden="true"
        />
      </div>
    </div>
  );
}
